import { run, resolveMotion, toMs } from '../motion-choreography';
import { mapStyles, markerIcon } from '../directory-map';

// The Figma 4-column size pattern, read row-first as one repeating 16-cell block
// and applied by each card's *visible* grid slot. With fixed row-first placement
// (column = slot % columns) this reproduces the exact designed rhythm:
//   LG SM MD LG
//   SM MD LG SM
//   MD SM MD SM
//   MD LG SM MD
// Tune the cadence here — the packer stays in lockstep.
const SIZE_PATTERN = ['lg', 'sm', 'md', 'lg', 'sm', 'md', 'lg', 'sm', 'md', 'sm', 'md', 'sm', 'md', 'lg', 'sm', 'md'];

// Popover icons, inlined so the map layer can build its cards without a Twig pass.
// (Mirror views/components/atoms/icons/icon-arrow.twig + icon-close.twig.)
const ARROW_SVG = `<svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.6887 11.3866H0V8.61335H14.6887L8.02243 1.94709L10 0L20 10L10 20L8.02243 18.0529L14.6887 11.3866Z" fill="currentColor"/></svg>`;
const CLOSE_SVG = `<svg width="14" height="14" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.28484 10.8271L3.12067 15.9679C2.76317 16.3254 2.33831 16.5081 1.84609 16.5158C1.35387 16.5239 0.921227 16.3412 0.548172 15.9679C0.190672 15.6104 0.0119218 15.1817 0.0119218 14.6817C0.0119218 14.1817 0.190672 13.7529 0.548172 13.3954L5.689 8.23125L0.548172 3.10875C0.190672 2.75125 0.008033 2.32639 0.00025522 1.83416C-0.00780034 1.34194 0.174838 0.909305 0.548172 0.53625C0.905672 0.17875 1.33442 0 1.83442 0C2.33442 0 2.76317 0.17875 3.12067 0.53625L8.28484 5.67708L13.4073 0.53625C13.7462 0.17875 14.1704 0 14.6798 0C15.189 0 15.6223 0.17875 15.9798 0.53625C16.3415 0.897639 16.5223 1.32736 16.5223 1.82542C16.5223 2.32347 16.3415 2.75125 15.9798 3.10875L10.839 8.23125L15.9798 13.3954C16.3373 13.7529 16.5161 14.1817 16.5161 14.6817C16.5161 15.1817 16.3373 15.6104 15.9798 15.9679C15.6184 16.3296 15.1887 16.5104 14.6907 16.5104C14.1926 16.5104 13.7648 16.3296 13.4073 15.9679L8.28484 10.8271Z" fill="currentColor"/></svg>`;

// Filter groups → the item `type` each narrows (grouped filter drawer + desktop
// pills share this one model).
const GROUP_TYPE = { organizations: 'organization', artists: 'artist', 'public-art': 'public_art' };

document.addEventListener('alpine:init', () => {
  Alpine.data('directory', () => {
    // Held in the closure, not on the reactive `this`, so Alpine never proxies
    // the DOM nodes (a proxied node breaks appendChild et al).
    let cardNodes = [];
    let calloutNodes = [];
    // Static slug→name lookup per sub-filter taxonomy (artist discipline, studio
    // location, artwork type), for the desktop dropdown labels (BugHerd #107,
    // #108). Built once from the rendered options.
    let subLabelMap = {};
    // Also closure-held: the active popover carries a live DOM node (.root), so a
    // reactive proxy would break its append/remove. The overlay class + fetched
    // glyph markup live here too — plain caches, no need to be reactive.
    let activePopover = null;
    let PopoverOverlay = null;
    let map = null;
    // Mobile overlay internals — DOM/Animation handles kept out of reactive `this`.
    let mapVisible = null; // resolves on the map's first 'idle' (tiles painted)
    let fieldAnim = null;
    let canvasAnim = null;
    let fieldRect = null; // the trigger's footprint — the field grows from / back to it
    // Pin-select card swipe/gesture state (non-reactive).
    let sheetBusy = false; // a drawer/card swap is mid-flight
    let dragStartY = 0;
    let dragY = 0;
    let dragging = false;
    // Org-list drawer expand/collapse drag state (non-reactive).
    let drawerStartY = 0;
    let drawerStartMax = 0;
    let drawerDragging = false;
    let drawerDragged = false;

    return {
      // Unified filter model — a group (org / artist / public-art, or null for
      // "everything") plus the sub-slugs it's narrowed to, keyed by taxonomy:
      // { artist_discipline: [...], studio_location: [...] }. A group can expose
      // more than one taxonomy section (Artists = discipline + studio location,
      // BugHerd #161); selections narrow with AND across taxonomies, OR within
      // one. Desktop pills and the mobile drawer both drive these; the drawer
      // edits a staged copy first.
      filterGroup: null,
      filterSubs: {},
      stagedGroup: 'all',
      stagedSubs: {},
      openSub: null, // taxonomy of the open desktop sub-filter dropdown, or null
      resultCount: null, // visible card count (null until first pack) — drives the empty state
      search: '',
      sortAsc: true,
      sortActive: false,
      view: 'grid',
      filtersOpen: false,
      mapFilterOpen: false, // the mobile map's own flat filter drawer
      mapFilterStaged: 'all', // single staged selection for the map filter
      canScrollUp: false,
      canScrollDown: true,
      markers: [],
      activeMarkerId: null,
      mapReady: false,
      mobileMapOpen: false,
      mobileCardId: null, // id of the pin whose card is showing (mobile), or null
      drawerExpanded: false, // the org-list drawer is expanded (vs resting at peek)

      // ---------------------------------------------------------------- Grid

      // Callouts flow in whenever the grid is unfiltered and unsearched. They
      // stay pinned at their fixed slots even while sorting (BugHerd #109) —
      // filtering or searching changes the visible set, so they drop out then.
      showCallouts() {
        return this.filterGroup === null && !this.search.trim();
      },

      columnCount() {
        if (window.matchMedia('(min-width: 1024px)').matches) return 4;
        if (window.matchMedia('(min-width: 768px)').matches) return 2;
        return 1;
      },

      // Build the column elements for the current breakpoint (only rebuilds when
      // the count changes). Cards are parked in the pool first so Alpine never
      // sees them detached mid-teardown.
      ensureColumns(cols) {
        const grid = this.$refs.grid;
        const existing = [...grid.children];
        if (
          existing.length === cols &&
          existing.every((c) => c.classList.contains('c-directory__column'))
        ) {
          return existing;
        }
        [...cardNodes, ...calloutNodes].forEach((n) => this.$refs.pool.appendChild(n));
        grid.replaceChildren();
        const columns = [];
        for (let i = 0; i < cols; i++) {
          const col = document.createElement('div');
          col.className = 'c-directory__column';
          grid.appendChild(col);
          columns.push(col);
        }
        return columns;
      },

      // Visible cards in the active sort order, with callouts spliced into their
      // fixed slots when the grid is pristine.
      sequence() {
        let visible = cardNodes.filter((n) => this.isVisible(n));
        if (this.sortActive) {
          visible = [...visible].sort((a, b) => {
            const cmp = (a.dataset.title || '').localeCompare(b.dataset.title || '');
            return this.sortAsc ? cmp : -cmp;
          });
        }
        if (!this.showCallouts()) return visible;

        const seq = [...visible];
        calloutNodes
          .filter((c) => parseInt(c.dataset.slot, 10) > 0)
          .sort((a, b) => parseInt(a.dataset.slot, 10) - parseInt(b.dataset.slot, 10))
          .forEach((c) => {
            const idx = Math.min(parseInt(c.dataset.slot, 10) - 1, seq.length);
            seq.splice(idx, 0, c);
          });
        return seq;
      },

      // Fixed row-first placement: slot i → column i % cols, size from the
      // pattern at that slot. This reproduces the exact designed adjacencies,
      // and because we re-pack the *visible* set each time it never leaves
      // interior holes (only the bottom edge is ragged, per the pattern).
      place() {
        const grid = this.$refs.grid;
        if (!grid.clientWidth) return; // hidden (map view) — defer until visible
        const cols = this.columnCount();
        const columns = this.ensureColumns(cols);
        const seq = this.sequence();

        // Reactive count of matching cards (callouts excluded) for the empty state.
        this.resultCount = cardNodes.filter((n) => this.isVisible(n)).length;

        // Clear lingering reveal animations so their held end-state doesn't stack.
        [...cardNodes, ...calloutNodes].forEach((n) =>
          n.getAnimations?.().forEach((a) => a.cancel())
        );

        seq.forEach((node, i) => {
          node.dataset.size = SIZE_PATTERN[i % SIZE_PATTERN.length];
          columns[i % cols].appendChild(node);
        });

        const placed = new Set(seq);
        [...cardNodes, ...calloutNodes].forEach((n) => {
          if (!placed.has(n)) this.$refs.pool.appendChild(n);
        });
      },

      // Re-pack. `reveal` plays the Tier 3 staggered entrance for user actions.
      repack({ reveal = false } = {}) {
        this.place();
        if (reveal) {
          [...this.$refs.grid.children].forEach((col) =>
            run('choreography-list-reveal', col)
          );
        }
      },

      // ------------------------------------------------------ Filter / sort

      // Repack the grid and keep the map markers in sync after any filter change.
      afterFilterChange() {
        this.repack({ reveal: true });
        if (this.mapReady) this.$nextTick(() => this.syncMarkers());
      },

      // Desktop pills — single-select shortcuts into the shared grouped model.
      // Org categories live under their taxonomy key so the grouped matcher
      // treats them like the drawer's org section.
      setFilter(slug) {
        if (slug === 'all') {
          this.filterGroup = null;
          this.filterSubs = {};
        } else if (slug === 'artist') {
          this.filterGroup = 'artists';
          this.filterSubs = {};
        } else if (slug === 'public-art') {
          this.filterGroup = 'public-art';
          this.filterSubs = {};
        } else {
          this.filterGroup = 'organizations';
          this.filterSubs = { organization_categories: [slug] };
        }
        this.openSub = null; // a group switch clears any open desktop dropdown
        this.afterFilterChange();
      },

      // The top pills mark the active group; the Artists dropdowns show any
      // sub-narrowing, so the Artists pill stays active while a discipline /
      // studio location is applied (it doesn't de-highlight on sub-select).
      isFilterActive(slug) {
        if (slug === 'all') return this.filterGroup === null;
        if (slug === 'artist') return this.filterGroup === 'artists';
        if (slug === 'public-art') return this.filterGroup === 'public-art';
        return (
          this.filterGroup === 'organizations' &&
          (this.filterSubs.organization_categories || []).includes(slug)
        );
      },

      // The groups that expose desktop sub-filter dropdowns in place of the
      // grid/map toggle: Artists (discipline + studio location) and Public Art
      // (artwork type). Only in grid view — Public Art stays mappable, so in map
      // view the toggle returns (BugHerd #107, #108).
      subfiltersActive() {
        return (
          this.view === 'grid' &&
          (this.filterGroup === 'artists' || this.filterGroup === 'public-art')
        );
      },

      // Which top pills are disabled. Artists has no map, so it's disabled in map
      // view. And while a sub-filterable group (Artists / Public Art) owns the
      // grid, the other top-level filters don't combine with it — you narrow via
      // its dropdowns — so they're disabled, leaving only "All" (the reset) and
      // the active group's own pill live. Matches the artist pill's map-view
      // disabled treatment (dashed, faded). (BugHerd #107, #108.)
      isFilterDisabled(slug) {
        if (slug === 'all') return false;
        if (slug === 'artist' && this.view === 'map') return true;
        if (this.subfiltersActive()) {
          const activeSlug = this.filterGroup === 'artists' ? 'artist' : 'public-art';
          return slug !== activeSlug;
        }
        return false;
      },

      // ------------------------ Desktop Artists / Public Art sub-filters (#107, #108)

      // Single dropdown open at a time; the pill toggles its own menu.
      toggleDropdown(taxonomy) {
        this.openSub = this.openSub === taxonomy ? null : taxonomy;
      },

      // Single-select within a taxonomy — empty slug = "All" (clears that
      // dimension). Writes straight to the applied grouped model (desktop has no
      // staging/Apply), so isVisible's AND-across-taxonomies matcher combines the
      // dropdowns. The dropdown is only reachable while its group is active, so
      // the group is already set — no need to touch filterGroup here. Shares
      // filterSubs with the mobile drawer.
      selectDesktopSub(taxonomy, slug) {
        this.filterSubs = { ...this.filterSubs, [taxonomy]: slug ? [slug] : [] };
        this.openSub = null;
        this.afterFilterChange();
      },

      desktopSubActive(taxonomy, slug) {
        return (this.filterSubs[taxonomy] || []).includes(slug);
      },

      desktopSubAllActive(taxonomy) {
        return !(this.filterSubs[taxonomy] && this.filterSubs[taxonomy].length);
      },

      // Pill label: the selected term's name, or the section label when unset.
      desktopSubLabel(taxonomy, fallback) {
        const sel = this.filterSubs[taxonomy] || [];
        if (!sel.length) return fallback;
        return (subLabelMap[taxonomy] && subLabelMap[taxonomy][sel[0]]) || fallback;
      },

      // Full reset for the empty-state "Clear all filters" button (grid view,
      // desktop + mobile). Clears the group, sub-filters and search in one go.
      resetFilters() {
        this.search = ''; // the search $watch repacks
        this.setFilter('all'); // clears group + subs + openSub, repacks + markers
      },

      toggleSort() {
        this.sortAsc = !this.sortAsc;
        this.sortActive = true;
        // Re-place in the new order without replaying the full staggered entrance
        // — that reveal read as a lag on every click (BugHerd #109). Callouts stay
        // pinned at their slots (showCallouts ignores sort state).
        this.repack({ reveal: false });
      },

      isVisible(el) {
        const type = el.dataset.type;
        const title = el.dataset.title || '';

        if (this.filterGroup) {
          if (type !== GROUP_TYPE[this.filterGroup]) return false;
          // One selected-slug array per narrowed taxonomy. The item must match at
          // least one slug in EVERY narrowed taxonomy (AND across sections) while
          // any single taxonomy is satisfied by any one of its slugs (OR within).
          const taxa = Object.values(this.filterSubs).filter((a) => a && a.length);
          if (taxa.length) {
            const slugs = el.dataset.filterSlugs ? el.dataset.filterSlugs.split(',') : [];
            if (!taxa.every((sel) => sel.some((s) => slugs.includes(s)))) return false;
          }
        }

        if (this.search.trim()) {
          if (!title.toLowerCase().includes(this.search.toLowerCase())) return false;
        }

        return true;
      },

      // ---------------------------------------------------- Filter drawer

      // Deep-ish copy of a grouped-subs object (one level of arrays), dropping
      // empty taxonomies so isVisible only ever sees active narrowings.
      cloneSubs(subs) {
        const out = {};
        for (const tax in subs) {
          if (subs[tax] && subs[tax].length) out[tax] = [...subs[tax]];
        }
        return out;
      },

      openFilters() {
        // Seed the staged selection from what's currently applied (null = "All").
        this.stagedGroup = this.filterGroup || 'all';
        this.stagedSubs = this.cloneSubs(this.filterSubs);
        this.filtersOpen = true;
        document.body.style.overflow = 'hidden';
        window.lenis?.stop();
        requestAnimationFrame(() => {
          const root = this.$refs.filterDrawer;
          if (root) run('choreography-filter-drawer-enter', root);
        });
      },

      closeFilters() {
        const root = this.$refs.filterDrawer;
        const finish = () => {
          this.filtersOpen = false;
          document.body.style.overflow = '';
          window.lenis?.start();
        };
        root ? run('choreography-filter-drawer-exit', root).then(finish) : finish();
      },

      // Switching group resets the sub-selection (each group has its own terms).
      setStagedGroup(group) {
        if (this.stagedGroup === group) return;
        this.stagedGroup = group;
        this.stagedSubs = {};
      },

      // Toggle a term within its taxonomy's staged selection. Reassigns the array
      // (rather than mutating in place) so the pill bindings react reliably.
      toggleSub(taxonomy, slug) {
        const arr = this.stagedSubs[taxonomy] || [];
        this.stagedSubs[taxonomy] = arr.includes(slug)
          ? arr.filter((s) => s !== slug)
          : [...arr, slug];
      },

      isSubActive(taxonomy, slug) {
        return (this.stagedSubs[taxonomy] || []).includes(slug);
      },

      // The "All" pill = no sub-narrowing within that taxonomy's section.
      stagedAllActive(taxonomy) {
        return !(this.stagedSubs[taxonomy] && this.stagedSubs[taxonomy].length);
      },

      selectStagedAll(taxonomy) {
        this.stagedSubs[taxonomy] = [];
      },

      applyFilters() {
        // 'all' is the drawer's representation of the everything state (filterGroup=null).
        this.filterGroup = this.stagedGroup === 'all' ? null : this.stagedGroup;
        this.filterSubs = this.cloneSubs(this.stagedSubs);
        this.afterFilterChange();
        this.closeFilters();
      },

      // "Clear all" removes every filter (back to everything) and dismisses.
      clearFilters() {
        this.filterGroup = null;
        this.filterSubs = {};
        this.stagedGroup = 'all';
        this.stagedSubs = {};
        this.afterFilterChange();
        this.closeFilters();
      },

      // ---------------------------------------------- Map filter drawer

      // The map filter is a single-select group radio (All / Organizations /
      // Artists[disabled] / Public Art) mirroring the grid drawer — Public Art is
      // a first-class map filter (BugHerd #159). Map the applied filter back to
      // its group for seeding on open (Artists never applies here — no pins).
      currentMapGroup() {
        if (this.filterGroup === 'organizations') return 'organizations';
        if (this.filterGroup === 'public-art') return 'public-art';
        return 'all';
      },

      // Opens over the map (which already owns the body-scroll lock), reusing the
      // filter-drawer enter/exit choreography.
      openMapFilter() {
        this.mapFilterStaged = this.currentMapGroup();
        this.mapFilterOpen = true;
        requestAnimationFrame(() => {
          const root = this.$refs.mapFilterDrawer;
          if (root) run('choreography-filter-drawer-enter', root);
        });
      },

      closeMapFilter() {
        const root = this.$refs.mapFilterDrawer;
        const finish = () => (this.mapFilterOpen = false);
        root ? run('choreography-filter-drawer-exit', root).then(finish) : finish();
      },

      setMapFilterStaged(group) {
        this.mapFilterStaged = group;
      },

      isMapFilterStaged(group) {
        return this.mapFilterStaged === group;
      },

      // Commit the staged group straight to the shared filter model ('all' → no
      // group). afterFilterChange re-syncs the map markers (the grid is hidden in
      // map view). No sub-filters at the group level here.
      applyMapFilter() {
        this.filterGroup = this.mapFilterStaged === 'all' ? null : this.mapFilterStaged;
        this.filterSubs = {};
        this.afterFilterChange();
        this.closeMapFilter();
      },

      clearMapFilter() {
        this.mapFilterStaged = 'all';
        this.filterGroup = null;
        this.filterSubs = {};
        this.afterFilterChange();
        this.closeMapFilter();
      },

      // ------------------------------------------- Mobile map (full-screen)

      // Entry point for the floating mobile trigger. Google Maps is heavy, so it's
      // lazy-loaded on first open with the magenta field standing in as the loading
      // state. The open then choreographs in four beats: expand the field from the
      // trigger → crossfade the map in once its tiles are ready → drop the header →
      // raise the list drawer.
      async openMobileMap() {
        if (this.mobileMapOpen) return;

        // Snapshot the trigger's footprint now (before it hides) so the field can
        // grow out of the exact pill the user tapped — the button's background
        // "taking over" the screen.
        const trigger = this.$root.querySelector('.c-directory__map-trigger');
        const rect = trigger?.getBoundingClientRect();
        fieldRect = rect && rect.width ? this.rectSnapshot(rect) : this.fallbackRect();

        // Artists have no map location, so that filter would empty the map — fall
        // back to "All" (mirrors the desktop view switch).
        if (this.filterGroup === 'artists') {
          this.filterGroup = null;
          this.filterSubs = {};
        }

        this.mobileMapOpen = true; // x-show reveals the (transparent) overlay
        document.body.style.overflow = 'hidden';
        window.lenis?.stop();

        // Warm the Maps script over the network now (non-blocking), but hold the
        // heavy map *build* until after the expand so it can't jank the clip
        // animation on the main thread.
        window._loadGoogleMaps?.();

        await this.$nextTick();
        const root = this.$refs.mapView;

        // Clean baseline — clear any held/leaked transforms from a prior session so
        // the drawer/header/card can't flash in ahead of the choreography.
        this.resetMobileChrome();

        // 1. Expand the magenta field out of the tapped button.
        await this.expandField();

        // 2. Hold a beat, then raise the morphing loader over the field — it's the
        //    loading state while the (heavy) map builds behind it.
        await this.pause('quick'); // 100ms
        Alpine.store('loader').show('mobileMap');

        // 3. Build the map behind the loader and wait for its first tiles to paint.
        await this.initMap();
        if (mapVisible) await mapVisible;

        // 4. Beat, then crossfade the loader out as the map fades in.
        await this.pause(); // 160ms
        Alpine.store('loader').hide('mobileMap');
        await this.crossfadeMap();

        // 5 + 6. Drop the header, then raise the drawer.
        await run('choreography-mobile-map-chrome-enter', root);

        this.$refs.mmBack?.focus();
      },

      // Reverse the open: lower the chrome, then collapse the field back toward the
      // trigger while the map fades out, then unmount + restore the page.
      async closeMobileMap() {
        if (!this.mobileMapOpen) return;
        const root = this.$refs.mapView;
        // If the user backs out mid-load, fade the loader out with the close.
        Alpine.store('loader').hide('mobileMap');

        // Lower whichever bottom sheet is up (the drawer, or the pin card) with the
        // header, then collapse the field.
        if (this.mobileCardId != null) {
          await Promise.all([
            this.runSheet('choreography-sheet-out', this.$refs.mmCard),
            this.runSheet('choreography-header-out', root.querySelector('.c-directory__mm-header')),
          ]);
        } else {
          await run('choreography-mobile-map-chrome-exit', root);
        }
        await this.collapseField();

        this.resetOverlayAnimations();
        this.resetMobileCard();
        this.mobileMapOpen = false;
        document.body.style.overflow = '';
        window.lenis?.start();
      },

      // Reconcile the two map experiences when the viewport crosses the desktop
      // breakpoint mid-session — each only renders at its own breakpoint, so hand
      // off cleanly rather than leaving a half-rendered map or a stuck overlay.
      handleViewportChange(isDesktop) {
        if (isDesktop) {
          // The full-screen overlay can't exist on desktop — tear it down and hand
          // off to the desktop split map for continuity.
          if (this.mobileMapOpen) {
            this.forceCloseMobileMap();
            this.view = 'map';
            this.$nextTick(() => {
              if (map && window.google) google.maps.event.trigger(map, 'resize');
            });
          }
          // The grid filter drawer is a mobile affordance (its trigger is hidden on
          // desktop) — never leave it stranded open over the desktop grid.
          if (this.filtersOpen) this.forceCloseFilters();
        } else if (this.view === 'map') {
          // The desktop split map can't render on mobile — fall back to the grid
          // (the floating trigger re-enters the mobile map).
          this.view = 'grid';
        }
      },

      // Cancel any held enter/exit choreography on a filter drawer so nothing is
      // left in a "shown" state after an instant close.
      cancelDrawerAnimations(root) {
        root
          ?.querySelectorAll('.c-filter-drawer, .c-filter-drawer-overlay')
          .forEach((el) => el.getAnimations?.().forEach((a) => a.cancel()));
      },

      // Instant, choreography-free teardown of the mobile overlay (used on a
      // viewport change) — restores the page and clears any held overlay state.
      forceCloseMobileMap() {
        this.mobileMapOpen = false;
        this.mapFilterOpen = false;
        document.body.style.overflow = '';
        window.lenis?.start();
        this.resetMobileChrome();
        this.cancelDrawerAnimations(this.$refs.mapFilterDrawer);
      },

      // Instant close of the grid filter drawer (used on a viewport change).
      forceCloseFilters() {
        this.filtersOpen = false;
        document.body.style.overflow = '';
        window.lenis?.start();
        this.cancelDrawerAnimations(this.$refs.filterDrawer);
      },

      // Plain-object copy of the trigger's rect (DOMRect isn't safe to stash).
      rectSnapshot(r) {
        return { top: r.top, right: r.right, bottom: r.bottom, left: r.left, height: r.height };
      },

      // Fallback footprint when the trigger can't be measured — a pill centred
      // 24px above the bottom edge, matching the trigger's resting position.
      fallbackRect() {
        const w = 128;
        const h = 44;
        const cx = window.innerWidth / 2;
        const bottom = window.innerHeight - 24;
        return { top: bottom - h, right: cx + w / 2, bottom, left: cx - w / 2, height: h };
      },

      // Centre of the tapped button + the radius a circle there must reach to
      // cover the whole screen (its farthest corner), in viewport coordinates.
      fieldCircle() {
        const r = fieldRect;
        const cx = (r.left + r.right) / 2;
        const cy = (r.top + r.bottom) / 2;
        const radius = Math.hypot(
          Math.max(cx, window.innerWidth - cx),
          Math.max(cy, window.innerHeight - cy)
        );
        return { cx, cy, radius };
      },

      // Size + pin the magenta circle so its centre sits exactly on the tapped
      // button and, at scale 1, it just covers the whole screen.
      placeField() {
        const field = this.$refs.mmField;
        const { cx, cy, radius } = this.fieldCircle();
        const d = radius * 2;
        field.style.width = `${d}px`;
        field.style.height = `${d}px`;
        field.style.left = `${cx - radius}px`;
        field.style.top = `${cy - radius}px`;
      },

      // Balloon the magenta circle out of the tapped button. Uses transform:scale
      // (GPU-composited, and its origin is unambiguous) rather than an animated
      // clip-path circle — WAAPI drops the `at <position>` on some engines (notably
      // iOS Safari) and falls back to a centred reveal.
      expandField() {
        this.placeField();
        const { duration, easing } = resolveMotion('extra-long emphasized');
        fieldAnim?.cancel();
        fieldAnim = this.$refs.mmField.animate(
          [{ transform: 'scale(0)' }, { transform: 'scale(1)' }],
          { duration: toMs(duration), easing, fill: 'both' }
        );
        return fieldAnim.finished.catch(() => {});
      },

      // Fade the map canvas in over the magenta field.
      crossfadeMap() {
        const canvas = this.$refs.mapCanvas;
        const { duration, easing } = resolveMotion('long standard');
        canvasAnim?.cancel();
        canvasAnim = canvas.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: toMs(duration),
          easing,
          fill: 'both',
        });
        return canvasAnim.finished.catch(() => {});
      },

      // Collapse the circle back down into the tapped button while the map fades
      // out — the reverse of the balloon.
      collapseField() {
        const field = this.$refs.mmField;
        const canvas = this.$refs.mapCanvas;
        const { duration, easing } = resolveMotion('medium exit');
        fieldAnim?.cancel();
        canvasAnim?.cancel();
        fieldAnim = field.animate(
          [{ transform: 'scale(1)' }, { transform: 'scale(0)' }],
          { duration: toMs(duration), easing, fill: 'both' }
        );
        canvasAnim = canvas.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: toMs(duration),
          easing,
          fill: 'both',
        });
        return Promise.all([
          fieldAnim.finished.catch(() => {}),
          canvasAnim.finished.catch(() => {}),
        ]);
      },

      // Drop the held field/canvas animations so the CSS initial state (field
      // clipped to nothing, canvas transparent) reasserts for the next open.
      resetOverlayAnimations() {
        [fieldAnim, canvasAnim].forEach((a) => a?.cancel());
        fieldAnim = canvasAnim = null;
      },

      // Force every overlay layer back to its hidden CSS baseline before an open.
      // The sheet choreographies hold their end-state with fill:'both', and a pin
      // swap can even resolve *after* a close (async race) and re-hold the drawer
      // "shown" — that leaked hold would otherwise override the CSS and make the
      // drawer appear at the bottom before its choreography step on the next open.
      resetMobileChrome() {
        const root = this.$refs.mapView;
        const layers = [
          this.$refs.mmField,
          this.$refs.mapCanvas,
          this.$refs.mmDrawer,
          this.$refs.mmCard,
          root?.querySelector('.c-directory__mm-header'),
        ];
        layers.forEach((el) => {
          if (!el) return;
          el.getAnimations?.().forEach((a) => a.cancel());
          el.style.transform = '';
          el.style.opacity = '';
        });
        // Revert any still-selected marker and clear the card.
        if (this.activeMarkerId != null) {
          const m = this.markers.find((mk) => mk._directoryItem.id === this.activeMarkerId);
          if (m) this.setMarkerActive(m, false);
        }
        if (this.$refs.mmCard) this.$refs.mmCard.innerHTML = '';
        // Collapse the org list back to its peek (the drawer is off-screen here,
        // so clearing the inline max-height can't flash a collapse).
        if (this.$refs.mapList) {
          this.$refs.mapList.style.maxHeight = '';
          this.$refs.mapList.style.transition = '';
        }
        this.drawerExpanded = false;
        this.mobileCardId = null;
        this.activeMarkerId = null;
        sheetBusy = false;
        // Clear any leaked loader state so each open starts from a clean baseline.
        Alpine.store('loader').hide('mobileMap');
      },

      // -------------------------------------------- Mobile pin-select card

      // Run a sheet choreography token on a specific element, first clearing any
      // inline transform (e.g. a leftover swipe offset) + prior animations so the
      // token's own from-frame drives the start.
      runSheet(name, el) {
        el.getAnimations?.().forEach((a) => a.cancel());
        el.style.transform = '';
        return run(name, el);
      },

      // A choreography beat, defaulting to 160ms (the `short` duration token) —
      // e.g. between the out and in sheet slides. Pass another duration token name
      // ('quick' = 100ms, etc.) for a different beat. Rides the token, so it
      // collapses to 0 under reduced motion.
      pause(name = 'short') {
        const { duration } = resolveMotion(`${name} standard`);
        return new Promise((resolve) => setTimeout(resolve, toMs(duration)));
      },

      // Tap a pin (mobile): slide the current sheet out, pause, then slide the pin's
      // card in. Whether the current sheet is the list drawer (first selection) or
      // another card (pin-to-pin), the beats are identical.
      async selectMobilePin(marker) {
        const item = marker._directoryItem;
        if (this.mobileCardId === item.id || sheetBusy) return;
        sheetBusy = true;

        // Invert the selected marker + pan to it.
        if (this.activeMarkerId != null) {
          const prev = this.markers.find((m) => m._directoryItem.id === this.activeMarkerId);
          if (prev) this.setMarkerActive(prev, false);
        }
        this.activeMarkerId = item.id;
        this.setMarkerActive(marker, true);
        map.panTo(marker.getPosition());

        const card = this.$refs.mmCard;
        const outgoing = this.mobileCardId != null ? card : this.$refs.mmDrawer;

        await this.runSheet('choreography-sheet-out', outgoing); // 240ms exit
        await this.pause(); // 160ms
        if (!this.mobileMapOpen) return void (sheetBusy = false); // closed mid-swap
        card.innerHTML = this.mobileCardMarkup(item);
        this.mobileCardId = item.id;
        await this.runSheet('choreography-sheet-in', card); // 360ms entrance
        sheetBusy = false;
      },

      // Dismiss the pin card (map tap or swipe-down) and bring the list drawer back.
      // `fromY` seeds the card's exit from a mid-swipe offset for a seamless flick.
      async dismissMobileCard(fromY = 0) {
        if (this.mobileCardId == null || sheetBusy) return;
        sheetBusy = true;

        const prev = this.markers.find((m) => m._directoryItem.id === this.mobileCardId);
        if (prev) this.setMarkerActive(prev, false);
        this.activeMarkerId = null;
        this.mobileCardId = null;

        const card = this.$refs.mmCard;
        if (fromY > 0) await this.sheetOutFrom(card, fromY);
        else await this.runSheet('choreography-sheet-out', card);
        card.innerHTML = '';
        card.style.transform = '';
        await this.pause();
        if (!this.mobileMapOpen) return void (sheetBusy = false); // closed mid-swap
        await this.runSheet('choreography-sheet-in', this.$refs.mmDrawer);
        sheetBusy = false;
      },

      // Slide an element out to the bottom starting from an arbitrary Y offset
      // (used to continue a swipe flick from where the finger let go).
      sheetOutFrom(el, fromY) {
        const { duration, easing } = resolveMotion('medium exit');
        el.getAnimations?.().forEach((a) => a.cancel());
        const a = el.animate(
          [{ transform: `translateY(${fromY}px)` }, { transform: 'translateY(100%)' }],
          { duration: toMs(duration), easing, fill: 'both' }
        );
        return a.finished.catch(() => {});
      },

      // Clear the pin card + revert its marker so the next open starts clean.
      resetMobileCard() {
        if (this.mobileCardId != null) {
          const prev = this.markers.find((m) => m._directoryItem.id === this.mobileCardId);
          if (prev) this.setMarkerActive(prev, false);
        }
        this.mobileCardId = null;
        this.activeMarkerId = null;
        sheetBusy = false;
        const card = this.$refs.mmCard;
        if (card) {
          card.getAnimations?.().forEach((a) => a.cancel());
          card.innerHTML = '';
          card.style.transform = '';
        }
        // Return the drawer to its hidden CSS state for the next open.
        this.$refs.mmDrawer?.getAnimations?.().forEach((a) => a.cancel());
      },

      // Tapping the card opens the full detail modal over the map — same behaviour
      // as clicking a grid card (card-directory.twig). The modal's own stacking
      // context (z 1100) sits above the map overlay (z 500). Guarded so a swipe
      // (not a tap) never triggers it.
      cardClick() {
        if (dragY > 10 || this.mobileCardId == null) return;
        Alpine.store('directoryModal').open(this.mobileCardId);
      },

      // ---- Swipe-to-dismiss the card -------------------------------------

      cardTouchStart(e) {
        if (this.mobileCardId == null || sheetBusy) return;
        dragStartY = e.touches[0].clientY;
        dragY = 0;
        dragging = true;
        // Take over from the (held) slide-in so we can drive transform directly.
        const card = this.$refs.mmCard;
        card.getAnimations?.().forEach((a) => a.cancel());
        card.style.transform = 'translateY(0)';
      },

      cardTouchMove(e) {
        if (!dragging) return;
        dragY = Math.max(0, e.touches[0].clientY - dragStartY);
        // Only hijack the gesture once it's clearly a downward drag, so vertical
        // intent is ours but taps still pass through as clicks.
        if (dragY > 4) e.preventDefault();
        this.$refs.mmCard.style.transform = `translateY(${dragY}px)`;
      },

      cardTouchEnd() {
        if (!dragging) return;
        dragging = false;
        const card = this.$refs.mmCard;
        const threshold = (card.offsetHeight || 320) * 0.3;
        if (dragY > threshold) {
          this.dismissMobileCard(dragY); // flick it the rest of the way out
        } else {
          // Snap back to rest, then hold via inline transform (the slide-in WAAPI
          // was cancelled on touchstart).
          card.getAnimations?.().forEach((a) => a.cancel());
          const { duration, easing } = resolveMotion('short entrance');
          const a = card.animate(
            [{ transform: `translateY(${dragY}px)` }, { transform: 'translateY(0)' }],
            { duration: toMs(duration), easing }
          );
          a.finished.catch(() => {}).then(() => {
            card.style.transform = 'translateY(0)';
            a.cancel();
          });
          dragY = 0;
        }
      },

      // ----------------------------------------------- Org list drawer

      // A mobile list entry opens the detail modal (like a grid card). Desktop
      // keeps its behaviour: pan the map to that marker.
      listEntryClick(id) {
        this.mobileMapOpen ? Alpine.store('directoryModal').open(id) : this.panToMarker(id);
      },

      // Max height the list expands to — leaves the header + a strip of map above
      // (design drawer ≈ 76vh).
      drawerExpandedMax() {
        return Math.max(200, Math.round(window.innerHeight * 0.75) - 88);
      },

      expandDrawer() {
        const list = this.$refs.mapList;
        list.style.transition = '';
        list.style.maxHeight = this.drawerExpandedMax() + 'px';
        this.drawerExpanded = true;
      },

      collapseDrawer() {
        const list = this.$refs.mapList;
        list.style.transition = '';
        list.style.maxHeight = '0px';
        this.drawerExpanded = false;
      },

      // Tab tap toggles the list (ignored right after a swipe, or while a card /
      // sheet swap owns the drawer).
      toggleDrawer() {
        if (drawerDragged || sheetBusy || this.mobileCardId != null) return;
        this.drawerExpanded ? this.collapseDrawer() : this.expandDrawer();
      },

      // ---- Swipe the tab to expand (up) / collapse (down) ----------------

      drawerTouchStart(e) {
        if (sheetBusy || this.mobileCardId != null) return;
        drawerStartY = e.touches[0].clientY;
        drawerStartMax = this.$refs.mapList.getBoundingClientRect().height;
        drawerDragging = true;
        drawerDragged = false;
        this.$refs.mapList.style.transition = 'none'; // 1:1 with the finger
      },

      drawerTouchMove(e) {
        if (!drawerDragging) return;
        const delta = drawerStartY - e.touches[0].clientY; // up = positive = expand
        if (Math.abs(delta) > 4) {
          drawerDragged = true;
          e.preventDefault();
        }
        const max = this.drawerExpandedMax();
        const h = Math.min(max, Math.max(0, drawerStartMax + delta));
        this.$refs.mapList.style.maxHeight = h + 'px';
      },

      drawerTouchEnd() {
        if (!drawerDragging) return;
        drawerDragging = false;
        this.$refs.mapList.style.transition = ''; // re-enable the CSS ease for the snap
        if (!drawerDragged) return; // a tap → let @click toggle
        const max = this.drawerExpandedMax();
        const current = this.$refs.mapList.getBoundingClientRect().height;
        current > max / 2 ? this.expandDrawer() : this.collapseDrawer();
      },

      // --------------------------------------------------------------- Map

      async initMap() {
        if (this.mapReady || !this.$refs.mapCanvas) return;

        try {
          await window._loadGoogleMaps();
        } catch {
          console.warn('Google Maps API key not found');
          return;
        }

        await this.buildMap();
      },

      async buildMap() {
        const canvas = this.$refs.mapCanvas;
        const items = JSON.parse(canvas.dataset.items || '[]');

        // Center on Bromo Arts District
        map = new google.maps.Map(canvas, {
          center: { lat: 39.2904, lng: -76.6175 },
          zoom: 15,
          styles: mapStyles(),
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        // Resolves once the first tiles have painted — the mobile crossfade waits
        // on this so the map never fades in over a blank canvas.
        mapVisible = new Promise((resolve) => {
          google.maps.event.addListenerOnce(map, 'idle', resolve);
        });

        items.forEach((item) => {
          const marker = new google.maps.Marker({
            position: { lat: item.lat, lng: item.lng },
            map,
            title: item.title,
            icon: this.iconFor(item),
          });
          marker._directoryItem = item;

          marker.addListener('click', () => {
            // Mobile: swap to the pin-select bottom card. Desktop: floating popover.
            if (this.mobileMapOpen) {
              this.selectMobilePin(marker);
            } else {
              this.openPopover(marker);
              this.scrollToListItem(item.id);
            }
          });

          this.markers.push(marker);
        });

        // Clicking the map background dismisses the card (mobile) / popover (desktop).
        map.addListener('click', () =>
          this.mobileMapOpen ? this.dismissMobileCard() : this.closePopover()
        );

        this.mapReady = true;
        this.syncMarkers();
      },

      // ------------------------------------------------------------- Markers

      // The google.maps icon descriptor for an item's category pin (shared with
      // the modal maps). `active` renders the inverted (selected) state.
      iconFor(item, active = false) {
        const cat = (item.categories || []).find((c) => c.icon);
        return markerIcon({
          glyph: cat ? cat.icon : null,
          colorToken: cat ? cat.icon_color : null,
          type: item.type,
          active,
        });
      },

      // Swap a marker between its default and inverted (selected) icon.
      setMarkerActive(marker, active) {
        marker.setIcon(this.iconFor(marker._directoryItem, active));
      },

      // ------------------------------------------------------------- Popover

      // Custom OverlayView popover — full DOM control (no InfoWindow chrome to
      // fight or override with !important) and clean Tier 3 modal choreography.
      popoverOverlay() {
        if (PopoverOverlay) return PopoverOverlay;
        const self = this;
        PopoverOverlay = class extends google.maps.OverlayView {
          constructor(item) {
            super();
            this.item = item;
            this.latLng = new google.maps.LatLng(item.lat, item.lng);
            this.root = null;
          }
          onAdd() {
            const root = document.createElement('div');
            root.className = 'c-directory__popover modal-root';
            root.innerHTML = self.popoverMarkup(this.item);
            this.root = root;
            this.getPanes().floatPane.appendChild(root);

            root.addEventListener('click', (e) => e.stopPropagation());
            // The whole card opens the modal; the arrow button is inside it so its
            // click bubbles up to here.
            root
              .querySelector('.c-directory__popover-card')
              ?.addEventListener('click', () =>
                Alpine.store('directoryModal').open(this.item.id)
              );
            // Close button is the one exception — swallow its click so it dismisses
            // the popover instead of bubbling up and opening the modal.
            root
              .querySelector('.c-directory__popover-close')
              ?.addEventListener('click', (e) => {
                e.stopPropagation();
                self.closePopover();
              });

            // Fade the root, pop the card — the shared modal choreography.
            run('choreography-modal-enter', root);
          }
          draw() {
            if (!this.root) return;
            const pos = this.getProjection().fromLatLngToDivPixel(this.latLng);
            if (!pos) return;
            this.root.style.left = `${pos.x}px`;
            this.root.style.top = `${pos.y}px`;
          }
          onRemove() {
            this.root?.remove();
            this.root = null;
          }
        };
        return PopoverOverlay;
      },

      // Category pills for a card. Caps at two full pills; extras collapse into a
      // +N pill (mirrors the grid card — the modal still lists every category).
      cardTags(item) {
        const cats = item.categories || [];
        const tagList = cats.slice(0, 2).map((c) => {
          const mark = c.icon
            ? `<span class="c-cat-icon c-directory__popover-tag-icon" style="--cat-color: var(${c.icon_color});">${c.icon}</span>`
            : `<span class="c-directory__popover-tag-dot c-directory__popover-tag-dot--${c.slug}"></span>`;
          return `<span class="c-directory__popover-tag">${mark}${c.name}</span>`;
        });
        if (cats.length > 2) {
          tagList.push(
            `<span class="c-directory__popover-tag c-directory__popover-tag--more">+${cats.length - 2}</span>`
          );
        }
        return tagList.join('');
      },

      cardImage(item) {
        return item.image
          ? `<img class="c-directory__popover-image" src="${item.image}" alt="" />`
          : '';
      },

      popoverMarkup(item) {
        return `
          <div class="c-directory__popover-card modal-card">
            ${this.cardImage(item)}
            <button class="c-directory__popover-close" aria-label="Close">${CLOSE_SVG}</button>
            <div class="c-directory__popover-overlay">
              <h3 class="c-directory__popover-title">${item.title}</h3>
              <div class="c-directory__popover-footer">
                <div class="c-directory__popover-tags">${this.cardTags(item)}</div>
                <button class="c-directory__popover-open" aria-label="View details">${ARROW_SVG}</button>
              </div>
            </div>
          </div>`;
      },

      // Mobile pin-select card — same visual language as the popover (image +
      // gradient overlay + title + tags + open arrow) but no close button; it's
      // dismissed by swiping down or tapping the map.
      mobileCardMarkup(item) {
        return `
          <div class="c-directory__mm-card-inner">
            ${this.cardImage(item)}
            <div class="c-directory__popover-overlay">
              <h3 class="c-directory__popover-title">${item.title}</h3>
              <div class="c-directory__popover-footer">
                <div class="c-directory__popover-tags">${this.cardTags(item)}</div>
                <button class="c-directory__popover-open" aria-label="View details">${ARROW_SVG}</button>
              </div>
            </div>
          </div>`;
      },

      openPopover(marker) {
        const item = marker._directoryItem;
        this.closePopover(); // reverts any previously-selected marker first
        this.activeMarkerId = item.id;
        map.panTo(marker.getPosition());
        this.setMarkerActive(marker, true); // invert: brand circle + light glyph
        const Overlay = this.popoverOverlay();
        activePopover = new Overlay(item);
        activePopover.setMap(map);
      },

      closePopover() {
        // Revert the outgoing marker to its default (light) icon.
        const prevId = this.activeMarkerId;
        this.activeMarkerId = null;
        if (prevId != null) {
          const prev = this.markers.find((m) => m._directoryItem.id === prevId);
          if (prev) this.setMarkerActive(prev, false);
        }
        const overlay = activePopover;
        if (!overlay) return;
        activePopover = null;
        const teardown = () => overlay.setMap(null);
        // Play the exit choreography on the live card, then unmount the overlay.
        overlay.root ? run('choreography-modal-exit', overlay.root).then(teardown) : teardown();
      },

      syncMarkers() {
        this.markers.forEach((marker) => {
          const item = marker._directoryItem;
          const el = document.createElement('div');
          el.dataset.type = item.type;
          el.dataset.filterSlugs = (item.filter_slugs || []).join(',');
          el.dataset.title = item.title;

          const visible = this.isVisible(el);
          marker.setVisible(visible);
        });

        // Dismiss the popover if its marker just got filtered out.
        if (activePopover && this.activeMarkerId) {
          const activeMarker = this.markers.find(
            (m) => m._directoryItem.id === this.activeMarkerId
          );
          if (activeMarker && !activeMarker.getVisible()) this.closePopover();
        }
      },

      panToMarker(id) {
        const marker = this.markers.find((m) => m._directoryItem.id === id);
        if (!marker) return;

        map.setZoom(16);
        this.openPopover(marker); // pans to the marker + reveals the card
      },

      scrollToListItem(id) {
        const list = this.$refs.mapList;
        if (!list) return;

        const item = list.querySelector(`[data-id="${id}"]`);
        if (item) {
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      },

      // --------------------------------------------------------- Lifecycle

      init() {
        // Snapshot the source nodes once, then let the packer own placement.
        const nodes = [...this.$refs.pool.querySelectorAll('.c-directory__item')];
        cardNodes = nodes.filter((n) => n.dataset.kind === 'item');
        calloutNodes = nodes.filter((n) => n.dataset.kind === 'callout');

        // Slug→name lookup for the desktop sub-filter dropdown labels (#107,
        // #108), sourced from the rendered option buttons so it can't drift.
        subLabelMap = {};
        this.$root
          .querySelectorAll('.c-directory__dropdown-option[data-slug]')
          .forEach((el) => {
            const tax = el.dataset.taxonomy;
            const slug = el.dataset.slug;
            if (!tax || !slug) return;
            (subLabelMap[tax] = subLabelMap[tax] || {})[slug] =
              el.dataset.name || el.textContent.trim();
          });

        // First pack once the grid is actually measurable (retry across frames
        // in case layout isn't settled yet), with the entrance stagger.
        const firstPack = () => {
          if (this.$refs.grid.clientWidth) this.repack({ reveal: true });
          else requestAnimationFrame(firstPack);
        };
        this.$nextTick(firstPack);

        // Re-pack when the column count changes at a breakpoint.
        ['(min-width: 768px)', '(min-width: 1024px)'].forEach((q) => {
          window.matchMedia(q).addEventListener('change', () => {
            if (this.view === 'grid') this.repack();
          });
        });

        // Keep the desktop + mobile map experiences from colliding across the
        // desktop breakpoint (see handleViewportChange).
        window
          .matchMedia('(min-width: 1024px)')
          .addEventListener('change', (e) => this.handleViewportChange(e.matches));

        this.$watch('view', (val) => {
          if (val === 'grid') this.$nextTick(() => this.place());
          if (val === 'map') {
            // Artists have no map location, so the Artists filter is disabled in
            // map view — clear it (fall back to "All") so the map isn't left empty.
            if (this.filterGroup === 'artists') {
              this.filterGroup = null;
              this.filterSubs = {};
            }
            this.$nextTick(() => {
              this.initMap();
              // Seed the scroll affordances from the real overflow so the down
              // indicator only shows when there's actually more to scroll.
              const list = this.$refs.mapList;
              if (list) {
                this.canScrollUp = list.scrollTop > 0;
                this.canScrollDown =
                  list.scrollTop + list.clientHeight < list.scrollHeight - 1;
              }
            });
          }
        });

        // Live search re-packs (no stagger) and keeps the map markers in sync.
        this.$watch('search', () => {
          this.repack();
          if (this.mapReady) this.syncMarkers();
        });
      },
    };
  });
});
